// src/course/CourseLanding.jsx

/* -------------------------------------------------------------------------- */
/* FULL FINAL CourseLanding.jsx — Option A + Tooltip Logic (Merged & Stable)  */
/* -------------------------------------------------------------------------- */

import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import {
  FaGraduationCap,
  FaChartBar,
  FaMoon,
  FaBrain,
  FaVideo,
  FaExclamationTriangle,
  FaPencilAlt,
  FaRedo,
  FaFire,
  FaCoins,
} from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { useUser } from "../sessionmanagement/UserContext";
import ProSubscriptionOverlay from "../course/ProSubscriptionOverlay";
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";

/* -------------------------------------------------------------------------- */
/*                          FIN TORRO STYLED COMPONENTS                       */
/* -------------------------------------------------------------------------- */

const SmartArrow = styled.div`
  font-size: 22px;
  text-align: center;
  margin-top: 8px;
  animation: arrowBounce 1.3s infinite ease-in-out;

  @keyframes arrowBounce {
    0% { transform: translateX(0); opacity: 0.8; }
    50% { transform: translateX(5px); opacity: 1; }
    100% { transform: translateX(0); opacity: 0.8; }
  }
`;



const SmartAnimatedArrow = styled.div`
  font-size: 22px;
  text-align: center;
  margin-top: 6px;
  animation: arrowBounce 1.35s infinite ease-in-out;

  @keyframes arrowBounce {
    0% { transform: translateY(0); opacity: 0.8; }
    50% { transform: translateY(-4px); opacity: 1; }
    100% { transform: translateY(0); opacity: 0.8; }
  }
`;



const TooltipWrapper = styled.div`
  animation: tooltipPop 0.22s ease-out forwards;
  transform-origin: center;

  @keyframes tooltipPop {
    0% { opacity: 0; transform: scale(0.85); }
    70% { opacity: 1; transform: scale(1.05); }
    100% { opacity: 1; transform: scale(1.0); }
  }
`;

const BounceArrow = styled.div`
  font-size: 22px;
  text-align: center;
  margin-top: 6px;
  animation: arrowBounce 1.35s infinite ease-in-out;

  @keyframes arrowBounce {
    0% { transform: translateY(0); opacity: 0.8; }
    50% { transform: translateY(-4px); opacity: 1; }
    100% { transform: translateY(0); opacity: 0.8; }
  }
`;




const StyledContainer = styled(Container)`
  background-color: #000;
  color: #fff;
  min-height: fit-content; /* THIS FIXES THE GAP */
  width: 100vw;
  max-width: 100vw !important;
  margin: 0;
  overflow-x: hidden;
  overflow-y: hidden; /* ensures the page ends exactly */
  font-family: "Nunito Sans", sans-serif;

 /* KILL Bootstrap’s bottom gaps */
 & > *:last-child {
   margin-bottom: 0 !important;
  padding-bottom: 0 !important;
 }

`;

const ChapterCard = styled(Card)`
  background: #ff7c24 !important;
  color: #ffffff !important;
  border-radius: 14px;
  padding: 16px 18px;
  text-align: center;
  font-size: 15px;
  font-weight: 800;
  margin: 12px auto;
  width: 100%;
  max-width: 680px;
  border: none;
`;

const TopicBox = styled(Card)`
  background: #ff7c24;
  color: #000;
  padding: 10px 16px;
  border-radius: 999px;
  text-align: center;
  font-weight: 700;
  margin: 10px auto 18px;
  max-width: 90%;
  font-size: 14px;
  line-height: 1.3;
  border: none;
`;

const LessonButton = styled(Button)`
  background-color: ${({ unlocked }) => (unlocked ? "#232830" : "#262a33")};
  color: ${({ unlocked }) => (unlocked ? "#fff" : "#777")};
  border-color: ${({ unlocked }) => (unlocked ? "#232830" : "#262a33")};
  font-size: 22px;
  border-radius: 16px;
  width: 60px;
  height: 60px;
  margin: 10px 15px;
  display: flex;
  justify-content: center;
  align-items: center;

  box-shadow: ${({ highlight }) =>
    highlight
      ? "0 0 0 2px #ffb800, 0 0 22px rgba(255,184,0,0.9)"
      : "0px 4px 10px rgba(0,0,0,0.6)"};

  transition: 0.2s ease-in-out;
`;

const CourseCard = styled.div`
  background-color: #16181d;
  color: #ffffff;
  border-radius: 14px;
  padding: 12px 14px;
  margin-bottom: 8px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition: 0.15s ease-in-out;
  &:hover {
    transform: scale(1.02);
    background-color: #20232b;
  }
`;

const LockedTopicsSection = styled.div`
  background-color: #12141a;
  color: #ccc;
  text-align: center;
  margin-top: 28px;
  padding: 18px 16px;
  border-radius: 14px;
  border: 1px dashed rgba(255, 255, 255, 0.16);

  h4 {
    color: #fff;
    font-size: 16px;
    margin-bottom: 6px;
    font-weight: 800;
  }
  p {
    font-size: 13px;
  }
`;

const HeaderStat = styled(Col)`
  cursor: pointer;
  padding: 0 8px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  position: relative;

  ${({ highlight }) =>
    highlight &&
    `
      box-shadow: 0 0 0 2px #ffb800, 0 0 22px rgba(255,184,0,0.9);
      padding: 4px 12px;
      border-radius: 999px;
      background: rgba(17, 24, 39, 0.9);
    `}

  .value {
    font-weight: 800;
    font-size: 14px;
  }
`;

/* -------------------------- Tooltip / Tour Components --------------------- */

const InlineCoachmark = styled.div`
  position: absolute;
  top: 110%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 40;
`;

const TourCard = styled.div`
  background: rgba(15, 23, 42, 0.96);
  color: #e5e7f3;
  padding: 12px 14px;
  border-radius: 14px;
  max-width: 360px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  box-shadow: 0 8px 26px rgba(0, 0, 0, 0.7);
  font-size: 13px;
  line-height: 1.4;
`;

const TourActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
`;

const TourButton = styled.button`
  padding: 6px 10px;
  border-radius: 10px;
  border: none;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;

  ${({ variant }) =>
    variant === "primary"
      ? `background: #ff7c24; color: #1a110a;`
      : `background: transparent; color: #e5e7f3;`}
`;

/* -------------------------------------------------------------------------- */
/*                              LESSON ICON MAP                               */
/* -------------------------------------------------------------------------- */

const getLessonIcon = (lessonTypeId) => {
  const icons = {
    "1": <FaMoon />,
    "2": <FaMoon />,
    "3": <FaMoon />,
    "4": <FaMoon />,
    "5": <FaMoon />,
    "6": <FaChartBar />,
    "7": <FaChartBar />,
    "8": <FaExclamationTriangle />,
    "9": <FaVideo />,
    "10": <FaPencilAlt />,
    "11": <FaBrain />,
    "12": <FaChartBar />,
    "13": <FaRedo />,
  };
  return icons[lessonTypeId] || <FaMoon />;
};

/* -------------------------------------------------------------------------- */
/*                        HARD-CODED PROP FUNDING DATA                        */
/* -------------------------------------------------------------------------- */

const PROP_CHAPTERS = [
  {
    chapterid: "propfunding-c1",
    chaptername: "Chapter 1 – Introduction to Prop Funding",
  },
];

const PROP_TOPICS = [
  {
    topicid: "propfunding-t1.1",
    chapterid: "propfunding-c1",
    topicname: "Why traders choose prop firms",
  },
  {
    topicid: "propfunding-t1.2",
    chapterid: "propfunding-c1",
    topicname: "How funded challenge accounts work",
  },
];

/* -------------------------------------------------------------------------- */
/*                        DYNAMIC ID GENERATORS                               */
/* -------------------------------------------------------------------------- */

const generateLessonId = (course, chapterIndex, lessonIndex) => {
  const prefix = course.toLowerCase();
  return `${prefix}-l${chapterIndex + 1}.${lessonIndex + 1}`;
};

const generateTopicId = (course, chapterIndex, topicIndex) => {
  const prefix = course.toLowerCase();
  return `${prefix}-t${chapterIndex + 1}.${topicIndex + 1}`;
};

/* -------------------------------------------------------------------------- */
/*                               FALLBACKS                                    */
/* -------------------------------------------------------------------------- */

const DEFAULT_FALLBACK_CHAPTER = (course) => ({
  chapterid: `${course.toLowerCase()}-c1`,
  chaptername: `Chapter 1 – Introduction to ${course}`,
});

const DEFAULT_FALLBACK_TOPIC = (course) => ({
  topicid: `${course.toLowerCase()}-t1.1`,
  chapterid: `${course.toLowerCase()}-c1`,
  topicname: `Why traders choose ${course}`,
});

/* -------------------------------------------------------------------------- */
/*                               MAIN COMPONENT                               */
/* -------------------------------------------------------------------------- */

const CourseLanding = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userDetails } = useUser() || {};

  const userid = userDetails?.userid ?? null;
  const streakCount = userDetails?.streakCount ?? 0;
  const totalTokens = userDetails?.totalTokens ?? 0;

  const [selectedCourse, setSelectedCourse] = useState("Crypto");
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  const [showCourses, setShowCourses] = useState(false);
  const [showProSubscription, setShowProSubscription] = useState(false);
  const [nextRecommendedLesson, setNextRecommendedLesson] = useState(
    "Start with the first lesson."
  );

  /* 🔥 Tooltip State */
  const [tourStep, setTourStep] = useState(null);

  const coursesRef = useRef(null);
  const db = getFirestore();

  /* ----------------------------- URL → Course ----------------------------- */

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const course = params.get("course");
    if (course) {
      setSelectedCourse(
        course.charAt(0).toUpperCase() + course.slice(1).toLowerCase()
      );
    }
  }, [location.search]);

  /* -------------------------- Start Product Tour -------------------------- */

  useEffect(() => {
    const seen = localStorage.getItem("courseLandingTourSeen");
    if (!seen) {
      setTourStep("course");
    }
  }, []);

  const advanceTour = () => {
    if (tourStep === "course") setTourStep("streak");
    else if (tourStep === "streak") setTourStep("tokens");
    else if (tourStep === "tokens") setTourStep("lesson");
    else setTourStep(null);
  };

  const endTour = () => {
    setTourStep(null);
    localStorage.setItem("courseLandingTourSeen", "1");
  };

  /* ---------------------------- Fetch Firestore --------------------------- */

  useEffect(() => {
    let isMounted = true;

    if (selectedCourse === "PropFunding") {
      setChapters(PROP_CHAPTERS);
      setTopics(PROP_TOPICS);
      return;
    }

    const fetchData = async () => {
      try {
        const lc = selectedCourse.toLowerCase();

        const chapterQ = query(
          collection(db, "chapters_new"),
          where("courseid", "==", lc)
        );

        const topicQ = query(
          collection(db, "topics_new"),
          where("courseid", "==", lc)
        );

        const chapterSnap = await getDocs(chapterQ);
        const topicSnap = await getDocs(topicQ);

        const fetchedChapters = chapterSnap.docs.map((d) => d.data());
        const fetchedTopics = topicSnap.docs.map((d) => d.data());

        if (isMounted) {
          if (fetchedChapters.length === 0 || fetchedTopics.length === 0) {
            setChapters([DEFAULT_FALLBACK_CHAPTER(selectedCourse)]);
            setTopics([DEFAULT_FALLBACK_TOPIC(selectedCourse)]);
          } else {
            setChapters(fetchedChapters);
            setTopics(fetchedTopics);
          }
        }
      } catch (err) {
        console.error(err);
        if (isMounted) {
          setChapters([DEFAULT_FALLBACK_CHAPTER(selectedCourse)]);
          setTopics([DEFAULT_FALLBACK_TOPIC(selectedCourse)]);
        }
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [db, selectedCourse]);

  /* -------------------------- Dropdown open/close ------------------------- */

  useEffect(() => {
    const handler = (e) => {
      if (coursesRef.current && !coursesRef.current.contains(e.target)) {
        setShowCourses(false);
      }
    };
    if (showCourses) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showCourses]);

  /* -------------------------- Next recommended lesson --------------------- */

  useEffect(() => {
    if (!userid) return;

    const loadNext = async () => {
      try {
        const q = query(
          collection(db, "userpracticesessions"),
          where("userid", "==", userid),
          orderBy("date", "desc"),
          limit(1)
        );
        const snap = await getDocs(q);

        if (snap.empty) {
          setNextRecommendedLesson("Start with the first lesson.");
          return;
        }

        const last = snap.docs[0].data().lessonid;
        const [prefix, rest] = last.split("-");
        const [_, num] = rest.replace("l", "").split(".");
        const next = (parseInt(num, 10) % 5) + 1;

        setNextRecommendedLesson(`${prefix}-l1.${next}`);
      } catch {
        setNextRecommendedLesson("Start with the first lesson.");
      }
    };

    loadNext();
  }, [db, userid]);


/* ------------------------------ Util 2️⃣ Force body to not scroll past content
 ----------------------------------- */
  useEffect(() => {
    document.body.style.overflowY = "hidden";
    return () => {
      document.body.style.overflowY = "auto";
    };
  }, []);

  const InnerContent = styled.div`
  display: flex;
  flex-direction: column;
   height: fit-content;
  padding-bottom: 0;
`;


useEffect(() => {
  document.body.style.backgroundColor = "#000";
  document.documentElement.style.backgroundColor = "#000";
  return () => {
    document.body.style.backgroundColor = "";
    document.documentElement.style.backgroundColor = "";
  };
}, []);



  /* ------------------------------ Util----------------------------------- */

  const isLessonUnlocked = (lessonId) =>
    [".1", ".2", ".3", ".4", ".5"].some((s) => lessonId.endsWith(s));

  /* ----------------------------- Render ----------------------------------- */

  return (
    <StyledContainer fluid>
      <InnerContent>
      {/* HEADER */}
      <Row className="text-center mb-4" style={{ marginTop: 4 }}>
        {/* COURSE */}
        <HeaderStat
          highlight={tourStep === "course"}
          onClick={() => {
            setShowCourses((s) => !s);
            if (tourStep === "course") advanceTour();
          }}
        >
          <FaGraduationCap style={{ color: "#FF7C24", fontSize: 22 }} />
          Course · {selectedCourse}

          {tourStep === "course" && (
            <InlineCoachmark onClick={(e) => e.stopPropagation()}>
              <TooltipWrapper>
              <TourCard>
                <strong>Select your learning track.</strong>
                <TourActions>
                  <TourButton onClick={endTour}>Skip</TourButton>
                  <TourButton variant="primary" onClick={advanceTour}>
                    Next
                  </TourButton>
                </TourActions>
               {tourStep !== "lesson" && (
  <SmartArrow>→</SmartArrow>
)}
              </TourCard>
              </TooltipWrapper>
            </InlineCoachmark>
          )}
        </HeaderStat>

        {/* STREAK */}
        <HeaderStat
          highlight={tourStep === "streak"}
          onClick={() => {
            if (tourStep === "streak") advanceTour();
          }}
        >
          <FaFire style={{ color: "#2ECC71", fontSize: 22 }} />
          Streak · {streakCount}

          {tourStep === "streak" && (
            <InlineCoachmark onClick={(e) => e.stopPropagation()}>
              <TourCard>
                <strong>Your streak shows consistent learning.</strong>
                <TourActions>
                  <TourButton onClick={endTour}>Skip</TourButton>
                  <TourButton variant="primary" onClick={advanceTour}>
                    Next
                  </TourButton>
                </TourActions>
              </TourCard>
            </InlineCoachmark>
          )}
        </HeaderStat>

        {/* TOKENS */}
        <HeaderStat
          highlight={tourStep === "tokens"}
          onClick={() => {
            if (tourStep === "tokens") advanceTour();
          }}
        >
          <FaCoins style={{ color: "#FFB800", fontSize: 22 }} />
          Tokens · {totalTokens}

          {tourStep === "tokens" && (
            <InlineCoachmark onClick={(e) => e.stopPropagation()}>
              <TourCard>
                <strong>Tokens reward your progress.</strong>
                <TourActions>
                  <TourButton onClick={endTour}>Skip</TourButton>
                  <TourButton variant="primary" onClick={advanceTour}>
                    Next
                  </TourButton>
                </TourActions>
              </TourCard>
            </InlineCoachmark>
          )}
        </HeaderStat>
      </Row>

      {/* DROPDOWN LIST */}
      {showCourses && (
        <div
          ref={coursesRef}
          style={{
            position: "absolute",
            top: 70,
            left: "50%",
            transform: "translateX(-50%)",
            width: "min(420px, 92vw)",
            zIndex: 20,
          }}
        >
          {[
            "Crypto",
            "Forex",
            "Psychology",
            "Investing",
            "Trading",
            "Technical Analysis",
            "Fundamental Analysis",
            "Stock Trading",
            "Commodities",
            "Futures",
            "PropFunding",
          ].map((course) => (
            <CourseCard key={course} onClick={() => setSelectedCourse(course)}>
              {course === selectedCourse ? "✅ " : ""} {course}
            </CourseCard>
          ))}
        </div>
      )}

      {/* CHAPTER + TOPICS */}
      {chapters.map((chapter, chapterIndex) => (
        <div key={chapter.chapterid}>
          <ChapterCard>{chapter.chaptername}</ChapterCard>

          {topics
            .filter((t) => t.chapterid === chapter.chapterid)
            .map((topic, topicIndex) => {
              const topicId =
                topic.topicid ||
                generateTopicId(selectedCourse, chapterIndex, topicIndex);

              const lessons = Array.from({ length: 13 }, (_, i) => ({
                id: generateLessonId(selectedCourse, chapterIndex, i),
                lessonTypeId: `${i + 1}`,
              }));

              /*
                 Look at the code in the file Course Landing Copy
              */

              const firstRow = [lessons[0], lessons[5], lessons[1]];
              const stem = [lessons[2], lessons[8]];

              return (
                <div key={topicId}>
                  <TopicBox>{topic.topicname}</TopicBox>

                  {/* TOP ROW */}
                  <div className="d-flex justify-content-center">
                    {firstRow.map((lesson, idx) => {
                      const isHighlighted =
                        tourStep === "lesson" &&
                        chapterIndex === 0 &&
                        topicIndex === 0 &&
                        idx === 0;

                      return (
                        <div
                          key={lesson.id}
                          style={{ position: "relative", display: "inline-flex" }}
                        >
                          <LessonButton
                            unlocked={isLessonUnlocked(lesson.id)}
                            highlight={isHighlighted}
                            onClick={() => {
                              if (tourStep === "lesson") {
                                advanceTour();
                              }
                              navigate(
                                `/practice-session-play/${lesson.id}/${topicId}`
                              );
                            }}
                          >
                            {getLessonIcon(lesson.lessonTypeId)}
                          </LessonButton>

                          {isHighlighted && (
                            <InlineCoachmark>
                              <TourCard>
                                <strong>Start your first lesson here.</strong>
                                <TourActions>
                                  <TourButton onClick={endTour}>Skip</TourButton>
                                  <TourButton
                                    variant="primary"
                                    onClick={advanceTour}
                                  >
                                    Finish
                                  </TourButton>
                                </TourActions>
                              </TourCard>
                            </InlineCoachmark>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* STEM */}
                  <div className="d-flex flex-column align-items-center">
                    {stem.map((lesson) => (
                      <LessonButton
                        key={lesson.id}
                        unlocked={isLessonUnlocked(lesson.id)}
                        onClick={() => {
                          if (tourStep === "lesson") advanceTour();
                          navigate(
                            `/practice-session-play/${lesson.id}/${topicId}`
                          );
                        }}
                      >
                        {getLessonIcon(lesson.lessonTypeId)}
                      </LessonButton>
                    ))}
                  </div>
                </div>
              );
            })}
        </div>
      ))}

      {/* LOCKED SECTION */}
      <LockedTopicsSection>
        <h4>🔒 Next Topics</h4>
        <p style={{ color: "#ddd" }}>
          Complete the lessons above to unlock advanced content.
        </p>
        <p style={{ color: "#ddd" }}>
          Next recommended: <strong>{nextRecommendedLesson}</strong>
        </p>
      </LockedTopicsSection>

      {/* PRO SUBSCRIPTION MODAL */}
      {showProSubscription && (
        <ProSubscriptionOverlay onClose={() => setShowProSubscription(false)} />
      )}
      </InnerContent>
    </StyledContainer>
  );
};



export default CourseLanding;
